import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Save, Eye, Edit } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initialize Turndown service for HTML to Markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
  bulletListMarker: '-',
  hr: '---',
  preformattedCode: false,
});
turndownService.use(gfm);

// Custom rule to ensure proper paragraph spacing
turndownService.addRule('paragraph', {
  filter: 'p',
  replacement: function (content) {
    return '\n\n' + content.trim() + '\n\n';
  }
});

// Custom rule for line breaks
turndownService.addRule('lineBreak', {
  filter: 'br',
  replacement: function () {
    return '\n\n';
  }
});

const ArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    category: 'Management/Leadership',
    article_type: 'article',
    image_url: '',
    external_url: '',
    status: 'draft'
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/articles/admin/${id}/`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
      alert('Erreur lors du chargement de l\'article');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode) {
      fetchArticle();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setFormData(prev => ({
      ...prev,
      title: newTitle,
      slug: generateSlug(newTitle)
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      alert('Type de fichier invalide. Utilisez: JPG, PNG, WebP ou GIF');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Fichier trop volumineux. Taille maximale: 5MB');
      return;
    }

    try {
      setUploading(true);
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API}/upload/image/`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploading(false);
      // Reset file input so same file can be re-selected
      e.target.value = '';
    }
  };

  const handlePaste = (e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    
    console.log('🔍 PASTE EVENT TRIGGERED');
    
    if (!clipboardData) {
      console.log('❌ No clipboard data available');
      return;
    }
    
    // Log all available clipboard types
    console.log('📋 Available clipboard types:', clipboardData.types);
    
    // Try to get HTML first (from Word, Google Docs, etc.)
    const htmlData = clipboardData.getData('text/html');
    const plainText = clipboardData.getData('text/plain');
    const rtfData = clipboardData.getData('text/rtf');
    
    console.log('HTML data length:', htmlData ? htmlData.length : 0);
    console.log('Plain text length:', plainText ? plainText.length : 0);
    console.log('RTF data length:', rtfData ? rtfData.length : 0);
    
    // If we have HTML data, convert it
    if (htmlData && htmlData.trim()) {
      e.preventDefault();
      
      console.log('✅ Processing HTML data');
      console.log('First 200 chars of HTML:', htmlData.substring(0, 200));
      
      try {
        // Clean up Word's messy HTML
        let cleanHtml = htmlData
          // Remove Word's XML namespaces and tags
          .replace(/<(\/)?(w|o|v):[^>]*>/gi, '')
          // Remove style attributes
          .replace(/\s*style="[^"]*"/gi, '')
          // Remove class attributes
          .replace(/\s*class="[^"]*"/gi, '')
          // Remove empty spans
          .replace(/<span[^>]*>\s*<\/span>/gi, '')
          // Remove comments
          .replace(/<!--[\s\S]*?-->/g, '');
        
        console.log('🧹 Cleaned HTML (first 200 chars):', cleanHtml.substring(0, 200));
        
        // Convert cleaned HTML to Markdown
        const markdown = turndownService.turndown(cleanHtml);
        
        // Clean up excessive blank lines (more than 2 consecutive)
        const cleanedMarkdown = markdown
          .replace(/\n{3,}/g, '\n\n')  // Replace 3+ newlines with 2
          .trim();
        
        console.log('✨ Converted to Markdown:', cleanedMarkdown.substring(0, 200));
        console.log('📏 Markdown length:', cleanedMarkdown.length);
        
        // Insert at cursor position
        const textarea = e.target;
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const currentContent = formData.content || '';
        
        const newContent = 
          currentContent.substring(0, start) + 
          cleanedMarkdown + 
          currentContent.substring(end);
        
        setFormData(prev => ({ ...prev, content: newContent }));
        
        console.log('✅ Content updated successfully');
        
        // Set cursor position after inserted markdown
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + cleanedMarkdown.length;
          textarea.focus();
        }, 0);
      } catch (error) {
        console.error('❌ Error converting HTML to Markdown:', error);
        // Fall back to plain text if conversion fails
      }
    } else {
      console.log('ℹ️ No HTML data, using default paste behavior (plain text)');
    }
  };

  const handleSubmit = async (status) => {
    if (!formData.title || !formData.content || !formData.excerpt) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.article_type === 'news' && !formData.external_url) {
      alert('Veuillez fournir l\'URL de l\'article externe');
      return;
    }

    try {
      setSaving(true);
      const dataToSubmit = { ...formData, status };

      if (isEditMode) {
        await axios.put(`${API}/articles/${id}/`, dataToSubmit);
      } else {
        await axios.post(`${API}/articles/`, dataToSubmit);
      }

      navigate('/admin/articles');
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="editor-loading">Chargement...</div>;
  }

  return (
    <div className="article-editor-page">
      <div className="editor-container">
        {/* Header */}
        <div className="editor-header">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/articles')}
          >
            <ArrowLeft size={18} />
            Retour
          </Button>
          <h1 className="editor-title">
            {isEditMode ? 'Modifier l\'article' : 'Nouvel article'}
          </h1>
        </div>

        {/* Form */}
        <div className="editor-content">
          <div className="editor-form">
            {/* Type d'article */}
            <div className="form-group">
              <Label htmlFor="article_type">Type de contenu *</Label>
              <Select
                value={formData.article_type}
                onValueChange={(value) => handleChange('article_type', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="article">Article original</SelectItem>
                  <SelectItem value="news">Actualité externe</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Titre */}
            <div className="form-group">
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="Le titre de votre article"
              />
            </div>

            {/* Slug */}
            <div className="form-group">
              <Label htmlFor="slug">Slug (URL) *</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="titre-de-larticle"
              />
              <p className="form-help">URL : /articles/{formData.slug || 'titre-article'}</p>
            </div>

            {/* Catégorie */}
            <div className="form-group">
              <Label htmlFor="category">Catégorie *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => handleChange('category', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Management/Leadership">Management/Leadership</SelectItem>
                  <SelectItem value="Coaching">Coaching</SelectItem>
                  <SelectItem value="Actualité">Actualité</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* URL externe (si actualité) */}
            {formData.article_type === 'news' && (
              <div className="form-group">
                <Label htmlFor="external_url">URL de l'article externe *</Label>
                <Input
                  id="external_url"
                  type="url"
                  value={formData.external_url}
                  onChange={(e) => handleChange('external_url', e.target.value)}
                  placeholder="https://..."
                />
              </div>
            )}

            {/* Image Upload */}
            <div className="form-group">
              <Label htmlFor="image">Image d'illustration (optionnel)</Label>
              <div className="image-upload-container">
                {formData.image_url ? (
                  <div className="image-preview-wrapper">
                    <img 
                      src={formData.image_url} 
                      alt="Aperçu" 
                      className="image-preview"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                      className="remove-image-btn"
                    >
                      ✕ Supprimer
                    </Button>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <input
                      type="file"
                      id="image-upload"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="image-upload" className="upload-label">
                      {uploading ? (
                        <div>⏳ Upload en cours...</div>
                      ) : (
                        <>
                          <div className="upload-icon">📸</div>
                          <div className="upload-text">
                            Cliquez pour choisir une image
                          </div>
                          <div className="upload-hint">
                            JPG, PNG, WebP ou GIF (max 5MB)
                          </div>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>
              <div className="form-help mt-2">
                Ou collez une URL d'image :
                <Input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => handleChange('image_url', e.target.value)}
                  placeholder="https://..."
                  className="mt-2"
                />
              </div>
            </div>

            {/* Résumé */}
            <div className="form-group">
              <Label htmlFor="excerpt">Résumé *</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => handleChange('excerpt', e.target.value)}
                placeholder="Un court résumé qui apparaîtra dans la liste des articles"
                rows={3}
              />
            </div>

            {/* Contenu avec Preview */}
            <div className="form-group">
              <Label>Contenu (Markdown) *</Label>
              <Tabs defaultValue="edit" className="editor-tabs">
                <TabsList>
                  <TabsTrigger value="edit">
                    <Edit size={16} className="mr-2" />
                    Éditer
                  </TabsTrigger>
                  <TabsTrigger value="preview">
                    <Eye size={16} className="mr-2" />
                    Prévisualiser
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="edit" className="tab-content">
                  <Textarea
                    value={formData.content}
                    onChange={(e) => handleChange('content', e.target.value)}
                    onPaste={handlePaste}
                    placeholder="# Votre titre

Votre contenu en Markdown...

## Sous-titre

**Texte en gras**, *italique*

- Liste à puces
- Item 2

[Lien](https://...)"
                    rows={20}
                    className="markdown-textarea"
                  />
                  <p className="form-help">
                    ✨ Collez directement depuis Word - la conversion se fait automatiquement ! | Markdown : **gras**, *italique*, # Titre, [lien](url)
                  </p>
                </TabsContent>
                
                <TabsContent value="preview" className="tab-content">
                  <Card className="markdown-preview">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {formData.content || '*Aucun contenu à prévisualiser*'}
                    </ReactMarkdown>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            {/* Actions */}
            <div className="editor-actions">
              <Button
                variant="outline"
                onClick={() => handleSubmit('draft')}
                disabled={saving}
              >
                <Save size={18} className="mr-2" />
                💾 Enregistrer brouillon (non visible publiquement)
              </Button>
              <Button
                onClick={() => handleSubmit('published')}
                disabled={saving}
                className="publish-button"
              >
                {saving ? 'Publication...' : '🚀 Publier (visible sur le site)'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleEditor;
